import { useState, useEffect, useCallback } from 'react'
import { Plus, Network, Edit, Trash2, Shield, Search, KeyRound, Building2, Wifi, Settings } from 'lucide-react'
import { useRole } from '../../hooks/useRole'
import { useTranslation } from 'react-i18next'
import { vpnNetworksApi } from '../../services/vpnNetworksApi'
import { adApi } from '../../services/adApi'
import { getTenantId } from '../../config/tenant'
import Modal from '../../components/Modal/Modal'
import { useAdConfigs } from '../../hooks/useAdDomains'

function normalizeVpnEndpoint(s) {
  return (s || '').trim().toLowerCase()
}

/** Portas já usadas no mesmo endpoint por outras redes (exclui networkId se edição) */
function getConflictingNetwork(networks, endpoint, port, excludeId) {
  const ep = normalizeVpnEndpoint(endpoint)
  const p = Number(port)
  if (!ep || !Number.isFinite(p)) return null
  return networks.find(
    (n) =>
      normalizeVpnEndpoint(n.serverEndpoint) === ep &&
      Number(n.listenPort) === p &&
      n.id !== excludeId
  ) || null
}

export default function Vpn() {
  const { t } = useTranslation()
  const [networks, setNetworks] = useState([])
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedNetwork, setSelectedNetwork] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    cidr: '',
    description: '',
    dnsServers: '',
    serverEndpoint: 'automais.io',
    listenPort: '',
    isDefault: false,
  })
  const { data: adConfigs } = useAdConfigs()
  const { canDelete } = useRole()
  const [formError, setFormError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [renewConfirmTarget, setRenewConfirmTarget] = useState(null)
  const [renewResult, setRenewResult] = useState(null)
  const [activeTab, setActiveTab] = useState('general') // 'general' | 'authGroups'
  const [authGroups, setAuthGroups] = useState([])
  const [authGroupsLoading, setAuthGroupsLoading] = useState(false)
  const [adGroupSearch, setAdGroupSearch] = useState('')
  const [adGroupResults, setAdGroupResults] = useState([])
  const [adGroupSearchLoading, setAdGroupSearchLoading] = useState(false)
  const [authGroupAddingId, setAuthGroupAddingId] = useState(null)
  const [authGroupRemovingId, setAuthGroupRemovingId] = useState(null)

  useEffect(() => {
    loadNetworks()
  }, [])

  const loadNetworks = async () => {
    try {
      setLoading(true)
      const tenantId = getTenantId()
      const data = await vpnNetworksApi.getByTenant(tenantId)
      setNetworks(data)
    } catch (error) {
      console.error('Erro ao carregar redes virtuais:', error)
      alert(t('vpn.errors.loadFailed') + ' ' + (error.message || t('vpn.errors.unknown')))
    } finally {
      setLoading(false)
    }
  }

  const loadAuthGroups = useCallback(async (networkId) => {
    if (!networkId) {
      setAuthGroups([])
      return
    }
    try {
      setAuthGroupsLoading(true)
      const data = await vpnNetworksApi.listAuthGroups(networkId)
      setAuthGroups(data || [])
    } catch (err) {
      console.error('Erro ao carregar grupos AD autorizados:', err)
      setAuthGroups([])
    } finally {
      setAuthGroupsLoading(false)
    }
  }, [])

  const handleSearchAdGroups = useCallback(async (term) => {
    setAdGroupSearch(term)
    if (!adConfigs?.length) {
      setAdGroupResults([])
      return
    }
    try {
      setAdGroupSearchLoading(true)
      // Listar grupos adotados (managed groups) de cada config AD do tenant; filtra localmente.
      // Só esses (que têm Guid no banco) podem ser autorizados — adoção prévia obrigatória.
      const allResults = []
      const t = (term || '').trim().toLowerCase()
      for (const cfg of adConfigs) {
        try {
          const groups = await adApi.getManagedGroups(cfg.id)
          for (const g of groups || []) {
            const name = (g.displayName || '').toLowerCase()
            const dn = (g.distinguishedName || '').toLowerCase()
            if (!t || name.includes(t) || dn.includes(t)) {
              allResults.push({ ...g, adConfigId: cfg.id, adConfigName: cfg.displayName })
            }
          }
        } catch (e) {
          console.warn(`Listagem de grupos adotados em ${cfg.displayName} falhou:`, e)
        }
      }
      setAdGroupResults(allResults)
    } finally {
      setAdGroupSearchLoading(false)
    }
  }, [adConfigs])

  const handleAddAuthGroup = async (managedAdGroupId) => {
    if (!selectedNetwork) return
    try {
      setAuthGroupAddingId(managedAdGroupId)
      await vpnNetworksApi.addAuthGroup(selectedNetwork.id, managedAdGroupId)
      await loadAuthGroups(selectedNetwork.id)
      setAdGroupSearch('')
      setAdGroupResults([])
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('vpn.errors.unknown')
      alert(msg)
    } finally {
      setAuthGroupAddingId(null)
    }
  }

  const handleRemoveAuthGroup = async (authGroupId) => {
    if (!selectedNetwork) return
    if (!confirm(t('vpn.authGroups.removeConfirm', 'Remover este grupo AD da lista de autorizados a autenticar nesta rede?'))) return
    try {
      setAuthGroupRemovingId(authGroupId)
      await vpnNetworksApi.removeAuthGroup(selectedNetwork.id, authGroupId)
      await loadAuthGroups(selectedNetwork.id)
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('vpn.errors.unknown')
      alert(msg)
    } finally {
      setAuthGroupRemovingId(null)
    }
  }

  const handleOpenModal = (network = null) => {
    setFormError('')
    setSelectedNetwork(network)
    setActiveTab('general')
    setAdGroupSearch('')
    setAdGroupResults([])
    if (network) {
      loadAuthGroups(network.id)
      const lp = network.listenPort != null && network.listenPort > 0 ? String(network.listenPort) : '51820'
      setFormData({
        name: network.name || '',
        slug: network.slug || '',
        cidr: network.cidr || '',
        description: network.description || '',
        dnsServers: network.dnsServers || '',
        serverEndpoint: network.serverEndpoint || 'automais.io',
        listenPort: lp,
        isDefault: network.isDefault || false,
      })
    } else {
      setAuthGroups([])
      setFormData({
        name: '',
        slug: '',
        cidr: '',
        description: '',
        dnsServers: '',
        serverEndpoint: 'automais.io',
        listenPort: '',
        isDefault: false,
      })
    }
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedNetwork(null)
    setFormError('')
    setFormData({
      name: '',
      slug: '',
      cidr: '',
      description: '',
      dnsServers: '',
      serverEndpoint: 'automais.io',
      listenPort: '',
      isDefault: false,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormError('')
    const tenantId = getTenantId()
    const endpoint = (formData.serverEndpoint || '').trim() || 'automais.io'

    if (selectedNetwork) {
      const port = parseInt(String(formData.listenPort).trim(), 10)
      if (!Number.isFinite(port) || port < 1 || port > 65535) {
        setFormError(t('vpn.errors.portRange'))
        return
      }
      const conflict = getConflictingNetwork(networks, endpoint, port, selectedNetwork.id)
      if (conflict) {
        setFormError(t('vpn.errors.portConflictUpdate', { port, network: conflict.name }))
        return
      }
      try {
        await vpnNetworksApi.update(selectedNetwork.id, {
          name: formData.name,
          description: formData.description,
          dnsServers: formData.dnsServers,
          serverEndpoint: endpoint,
          isDefault: formData.isDefault,
          listenPort: port,
        })
        handleCloseModal()
        loadNetworks()
      } catch (error) {
        console.error('Erro ao salvar rede virtual:', error)
        const msg = error.response?.data?.message || error.message || t('vpn.errors.unknown')
        setFormError(msg)
      }
      return
    }

    const createPayload = {
      name: formData.name,
      slug: formData.slug,
      cidr: formData.cidr,
      description: formData.description || undefined,
      dnsServers: formData.dnsServers || undefined,
      serverEndpoint: endpoint,
      isDefault: formData.isDefault,
    }
    const portStr = String(formData.listenPort || '').trim()
    if (portStr !== '') {
      const port = parseInt(portStr, 10)
      if (!Number.isFinite(port) || port < 1 || port > 65535) {
        setFormError(t('vpn.errors.portRange'))
        return
      }
      const conflict = getConflictingNetwork(networks, endpoint, port, null)
      if (conflict) {
        setFormError(t('vpn.errors.portConflictCreate', { port, network: conflict.name }))
        return
      }
      createPayload.listenPort = port
    }

    try {
      await vpnNetworksApi.create(tenantId, createPayload)
      handleCloseModal()
      loadNetworks()
    } catch (error) {
      console.error('Erro ao salvar rede virtual:', error)
      const msg = error.response?.data?.message || error.message || t('vpn.errors.unknown')
      setFormError(msg)
    }
  }

  const openRenewConfirm = (e, network) => {
    e?.stopPropagation?.()
    setRenewConfirmTarget(network)
  }

  const closeRenewConfirm = () => setRenewConfirmTarget(null)

  const handleConfirmRegenerateServerKeys = async () => {
    if (!renewConfirmTarget) return
    const network = renewConfirmTarget
    setRenewConfirmTarget(null)
    try {
      await vpnNetworksApi.regenerateServerKeys(network.id)
      await loadNetworks()
      setRenewResult({
        success: true,
        message: t('vpn.renew.successMessage'),
      })
    } catch (error) {
      console.error(error)
      setRenewResult({
        success: false,
        message: error.response?.data?.message || error.message || t('vpn.errors.unknown'),
      })
    }
  }

  const handleDelete = async (networkId) => {
    if (!confirm(t('vpn.removeConfirm'))) {
      return
    }
    try {
      await vpnNetworksApi.delete(networkId)
      loadNetworks()
    } catch (error) {
      console.error('Erro ao excluir rede virtual:', error)
      alert(t('vpn.removeError') + ' ' + (error.response?.data?.message || error.message || t('vpn.errors.unknown')))
    }
  }

  const filteredNetworks = networks.filter((network) => {
    const q = searchTerm.toLowerCase()
    return (
      network.name.toLowerCase().includes(q) ||
      network.cidr.toLowerCase().includes(q) ||
      (network.slug && network.slug.toLowerCase().includes(q)) ||
      String(network.listenPort || '').includes(q)
    )
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('vpn.loading')}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('vpn.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t('vpn.subtitle')}
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn btn-primary"
        >
          <Plus className="w-4 h-4" />
          {t('vpn.new')}
        </button>
      </div>

      {/* Search */}
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder={t('vpn.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 input"
        />
      </div>

      {/* Networks List */}
      {filteredNetworks.length === 0 ? (
        <div className="card p-12 text-center">
          <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('vpn.empty.none')}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm
              ? t('vpn.empty.searchHint')
              : t('vpn.empty.firstHint')}
          </p>
          {!searchTerm && (
            <button
              onClick={() => handleOpenModal()}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4" />
              {t('vpn.empty.firstCreate')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredNetworks.map((network) => (
            <div key={network.id} className="card p-6">
              <div className="flex gap-3 mb-4">
                <div className="icon-box shrink-0 self-start" style={{ background: 'var(--theme-icon-box-background, color-mix(in srgb, var(--theme-primary) 15%, transparent))' , color: 'var(--theme-icon-box-text, var(--theme-primary))' }}>
                  <Network className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">{network.name}</h3>
                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mt-1">
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                      {network.isDefault && (
                        <span className="badge badge-success">{t('vpn.badges.default')}</span>
                      )}
                      {!network.serverKeysConfigured && (
                        <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                          {t('vpn.badges.noServerKeys')}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={(e) => openRenewConfirm(e, network)}
                        className="p-2 hover:bg-amber-50 rounded-lg transition-colors"
                        title={t('vpn.renewKeys')}
                      >
                        <KeyRound className="w-4 h-4 text-amber-600" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOpenModal(network)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title={t('common.buttons.edit')}
                      >
                        <Edit className="w-4 h-4 text-gray-600" />
                      </button>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDelete(network.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title={t('common.buttons.delete')}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-600">CIDR:</span>{' '}
                  <span className="font-mono font-medium text-gray-900">{network.cidr}</span>
                </div>
                {network.slug && (
                  <div>
                    <span className="text-gray-600">Slug:</span>{' '}
                    <span className="font-mono text-gray-900">{network.slug}</span>
                  </div>
                )}
                {network.dnsServers && (
                  <div>
                    <span className="text-gray-600">DNS:</span>{' '}
                    <span className="text-gray-900">{network.dnsServers}</span>
                  </div>
                )}
                {network.serverEndpoint && (
                  <div>
                    <span className="text-gray-600">Endpoint:</span>{' '}
                    <span className="font-mono text-gray-900">{network.serverEndpoint}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-600">Porta UDP (túnel):</span>{' '}
                  <span className="font-mono font-medium text-theme-primary">
                    {network.listenPort != null && network.listenPort > 0 ? network.listenPort : 51820}
                  </span>
                </div>
                {network.description && (
                  <div className="text-gray-600 text-xs mt-2">
                    {network.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedNetwork ? t('vpn.modal.titleEdit') : t('vpn.modal.titleNew')}
      >
        {/* Tabs (só ao editar — criar não tem authGroups ainda) */}
        {selectedNetwork && (
          <div className="flex gap-2 mb-4 border-b border-gray-200">
            <button
              type="button"
              onClick={() => setActiveTab('general')}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${
                activeTab === 'general'
                  ? 'text-theme-primary border-b-2 border-theme-primary'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Settings className="w-4 h-4" />
              {t('vpn.modal.tabs.general', 'Geral')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('authGroups')}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${
                activeTab === 'authGroups'
                  ? 'text-theme-primary border-b-2 border-theme-primary'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Wifi className="w-4 h-4" />
              {t('vpn.modal.tabs.authGroups', 'Acesso AD')}
              <span className="ml-1 text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5">
                {authGroups.length}
              </span>
            </button>
          </div>
        )}

        {(!selectedNetwork || activeTab === 'general') && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
              role="alert"
            >
              {formError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('vpn.modal.nameLabel')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input w-full"
              placeholder="Ex: Rede Principal"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('vpn.modal.slugLabel')}
            </label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className="input w-full"
              placeholder="Ex: rede-principal"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('vpn.modal.cidrLabel')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.cidr}
              onChange={(e) => setFormData({ ...formData, cidr: e.target.value })}
              className="input w-full font-mono"
              placeholder="Ex: 10.100.1.0/24"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('vpn.modal.dnsLabel')}
            </label>
            <input
              type="text"
              value={formData.dnsServers}
              onChange={(e) => setFormData({ ...formData, dnsServers: e.target.value })}
              className="input w-full"
              placeholder="Ex: 8.8.8.8, 8.8.4.4"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('vpn.modal.serverEndpointLabel')}
            </label>
            <input
              type="text"
              value={formData.serverEndpoint}
              onChange={(e) => setFormData({ ...formData, serverEndpoint: e.target.value })}
              className="input w-full font-mono"
              placeholder="automais.io"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('vpn.modal.listenPortLabel')} {selectedNetwork && <span className="text-red-500">*</span>}
            </label>
            <input
              type="number"
              min={1}
              max={65535}
              value={formData.listenPort}
              onChange={(e) => setFormData({ ...formData, listenPort: e.target.value })}
              className="input w-full font-mono"
              required={!!selectedNetwork}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('vpn.modal.descriptionLabel')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full"
              rows="3"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isDefault"
              checked={formData.isDefault}
              onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              className="w-4 h-4"
            />
            <label htmlFor="isDefault" className="text-sm text-gray-700">
              {t('vpn.modal.isDefault')}
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCloseModal}
              className="btn btn-secondary"
            >
              {t('vpn.modal.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              {t('vpn.modal.save')}
            </button>
          </div>
        </form>
        )}

        {selectedNetwork && activeTab === 'authGroups' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('vpn.authGroups.description',
                'Liste os grupos AD que podem autenticar (e provisionar peer) nesta rede via Connect.IO. Apenas grupos adotados em algum domínio AD do tenant aparecem aqui.')}
            </p>

            {authGroupsLoading ? (
              <div className="text-sm text-gray-500 py-4 text-center">{t('vpn.loading')}</div>
            ) : authGroups.length === 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="font-medium">{t('vpn.authGroups.emptyTitle', 'Nenhum grupo AD autorizado.')}</p>
                <p className="mt-1 text-xs">
                  {t('vpn.authGroups.emptyHint',
                    'Sem entradas aqui, esta rede rejeita o login do Connect.IO via AD com 401. Adicione ao menos um grupo abaixo.')}
                </p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-64 overflow-y-auto">
                {authGroups.map((g) => (
                  <div key={g.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{g.adGroupDisplayName}</div>
                      <div className="text-xs text-gray-500 font-mono truncate">{g.adGroupDistinguishedName}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        <Building2 className="w-3 h-3 inline -mt-0.5 mr-1" />
                        {g.adConfigDisplayName}
                      </div>
                    </div>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => handleRemoveAuthGroup(g.id)}
                        disabled={authGroupRemovingId === g.id}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title={t('common.buttons.remove', 'Remover')}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('vpn.authGroups.addLabel', 'Adicionar grupo AD')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('vpn.authGroups.searchPlaceholder', 'Buscar entre os grupos adotados...')}
                  value={adGroupSearch}
                  onChange={(e) => handleSearchAdGroups(e.target.value)}
                  onFocus={() => { if (!adGroupResults.length) handleSearchAdGroups('') }}
                  className="w-full pl-10 input"
                />
              </div>
              {adGroupSearchLoading ? (
                <div className="text-xs text-gray-500 mt-2">{t('vpn.loading')}</div>
              ) : adGroupResults.length > 0 ? (
                <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto">
                  {adGroupResults.map((g) => {
                    const alreadyAdded = authGroups.some((a) => a.managedAdGroupId === g.id)
                    return (
                      <div key={g.id} className="flex items-center justify-between gap-3 px-4 py-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{g.displayName}</div>
                          <div className="text-xs text-gray-500 font-mono truncate">{g.distinguishedName}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            <Building2 className="w-3 h-3 inline -mt-0.5 mr-1" />
                            {g.adConfigName}
                          </div>
                        </div>
                        {alreadyAdded ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-green-50 text-green-700 border border-green-200 shrink-0">
                            {t('vpn.authGroups.alreadyAdded', 'Já autorizado')}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleAddAuthGroup(g.id)}
                            disabled={authGroupAddingId === g.id}
                            className="btn btn-secondary text-xs shrink-0 disabled:opacity-50"
                          >
                            <Plus className="w-3 h-3" />
                            {t('common.buttons.add', 'Adicionar')}
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : adGroupSearch.length > 0 ? (
                <p className="text-xs text-gray-500 mt-2">{t('vpn.authGroups.noResults', 'Nenhum grupo encontrado.')}</p>
              ) : null}
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button type="button" onClick={handleCloseModal} className="btn btn-secondary">
                {t('vpn.modal.cancel')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal: confirmar renovar chaves do servidor */}
      <Modal
        isOpen={!!renewConfirmTarget}
        onClose={closeRenewConfirm}
        title={t('vpn.renew.confirmTitle')}
      >
        {renewConfirmTarget && (
          <div className="space-y-4">
            <p className="text-gray-700">
              <strong>{renewConfirmTarget.name}</strong>
            </p>
            <p className="text-sm text-gray-600">
              {t('vpn.renew.confirmMessage')}
            </p>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button type="button" onClick={closeRenewConfirm} className="btn btn-secondary">
                {t('vpn.renew.cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmRegenerateServerKeys}
                className="btn btn-primary bg-amber-600 hover:bg-amber-700"
              >
                {t('vpn.renew.confirm')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal: resultado da renovação */}
      <Modal
        isOpen={!!renewResult}
        onClose={() => setRenewResult(null)}
        title={renewResult?.success ? t('vpn.renew.successTitle') : t('vpn.renew.errorTitle')}
      >
        {renewResult && (
          <div className="space-y-4">
            <p className={renewResult.success ? 'text-gray-700' : 'text-red-700'}>
              {renewResult.message}
            </p>
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button type="button" onClick={() => setRenewResult(null)} className="btn btn-primary">
                {t('vpn.renew.close')}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

